import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Award, MapPin, Camera, Users, Lock, Globe, Trophy } from "lucide-react";
import { api, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const overline = "hr-overline text-blaze text-[11px]";
const VIS_ICON = { public: Globe, party: Users, private: Lock };

export default function TrophyRoom() {
  const { isAuthed, ready, user } = useAuth();
  const { openAuth } = useUI();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ species: "", state: "", year: "", weapon: "" });

  useEffect(() => { if (isAuthed) api.trophyRoom().then(setData).catch(() => setData({ trophies: [] })); }, [isAuthed]);

  const trophies = data?.trophies || [];
  const filtered = useMemo(() => trophies.filter((t) =>
    (!filter.species || t.species === filter.species) &&
    (!filter.state || t.state === filter.state) &&
    (!filter.year || String(t.year) === String(filter.year)) &&
    (!filter.weapon || t.weapon === filter.weapon)
  ), [trophies, filter]);

  if (!ready || (isAuthed && !data)) return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;

  if (!isAuthed) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="max-w-lg mx-auto text-center py-24 px-5"><Trophy className="text-blaze mx-auto" size={30} /><h1 className="font-serif text-4xl text-bone mt-5">Trophy Room</h1><p className="text-bone/60 mt-3">Sign in to view your harvests.</p><button onClick={() => openAuth("login")} className="mt-6 bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold" data-testid="trophy-signin">Sign in</button></div></div>;
  }

  const filterOpts = [["species", "All species", data.species], ["state", "All states", data.states], ["year", "All years", data.years], ["weapon", "All weapons", data.weapons]];

  return (
    <div className="bg-charcoal min-h-screen grain text-bone" data-testid="trophy-room-page">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-10">
        <span className={overline}>{user?.display_name}</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2">Trophy Room</h1>
        <p className="text-bone/50 mt-2">Your harvests. The Passport celebrates every hunt — the Trophy Room celebrates the ones that filled a tag.</p>

        {trophies.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-white/15 rounded-xl" data-testid="trophy-empty">
            <Award className="text-bone/20 mx-auto" size={30} />
            <p className="text-bone/60 mt-3">No harvests logged yet. Mark a hunt as a harvest in your Hunt Passport and it&apos;ll appear here.</p>
            <button onClick={() => navigate("/passport")} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="trophy-to-passport">Open Hunt Passport</button>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="trophy-filters">
              {filterOpts.map(([k, label, opts]) => (
                <select key={k} value={filter[k]} onChange={(e) => setFilter({ ...filter, [k]: e.target.value })} className="bg-white/5 border border-white/15 rounded px-3.5 py-2 text-sm text-bone outline-none focus:border-blaze" data-testid={`trophy-filter-${k}`}>
                  <option value="" className="bg-charcoal">{label}</option>{opts.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                </select>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((t) => {
                const Vis = VIS_ICON[t.visibility] || Lock;
                return (
                  <motion.button key={t.hunt_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    onClick={() => navigate(`/hunts/${t.hunt_id}`)}
                    className="text-left bg-white/[0.04] border border-white/10 hover:border-blaze/50 rounded-xl overflow-hidden transition-colors group" data-testid={`trophy-${t.hunt_id}`}>
                    <div className="aspect-[4/3] bg-white/5 overflow-hidden relative">
                      {t.photos?.[0] ? <img src={mediaSrc(t.photos[0].url)} alt={t.species} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full grid place-items-center"><Camera size={26} className="text-bone/20" /></div>}
                      <span className="absolute top-2.5 right-2.5 bg-black/50 backdrop-blur rounded-full p-1.5" title={`${t.visibility} entry`}><Vis size={12} className="text-bone/90" /></span>
                      {t.score && <span className="absolute bottom-2.5 left-2.5 bg-forest text-bone text-xs font-bold px-2.5 py-1 rounded-full">{t.score}</span>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-serif text-xl text-bone">{t.species}</h3>
                      <p className="text-bone/50 text-sm mt-1 flex items-center gap-2 flex-wrap">
                        {t.year && <span>{t.year}</span>}
                        {t.state && <span className="flex items-center gap-1"><MapPin size={11} />{t.state}</span>}
                        {t.weapon && <span>· {t.weapon}</span>}
                      </p>
                      {t.party?.length > 0 && <p className="text-bone/40 text-xs mt-2 flex items-center gap-1.5"><Users size={11} /> {t.party.join(", ")}</p>}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </>
        )}
        <p className="text-bone/40 text-xs mt-10 flex items-center gap-1.5"><Lock size={11} /> Every entry is private by default. Set each one to Party or Public from its hunt record. Exact locations are never shared.</p>
      </main>
      <Footer />
    </div>
  );
}
